import { useState } from 'react';
import { apiFetch, NetworkError } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { enqueueOp } from '../../lib/offline/opQueue';
import { getBookingsReadStamp } from '../../lib/offline/cache';
import { stalenessFor } from '../../lib/offline/staleness';
import { C, F } from './theme';
import { ModalOverlay, Field, inputStyle } from './atoms';
import { StepUpFields } from '../admin/StepUp';
import type { Aircraft, Base } from '../../lib/opsBoardTypes';

interface Props {
  registration: string;
  aircraft: Aircraft | undefined;
  bases: Base[];
  // Optional preselected target base (e.g. the booking base from the save prompt).
  suggestIcao?: string;
  onClose: () => void;
  onDone: (warnings: string[]) => void;
}

// Relocate an aircraft's current base (ops/admin). POST /api/aircraft/:reg/relocate.
export default function RelocateModal({ registration, aircraft, bases, suggestIcao, onClose, onDone }: Props) {
  const { user } = useAuth(); // 5d-p2b: offline relocations queue per-user
  const baseIcaos = bases.filter((b) => b.icao);
  const [icao, setIcao] = useState<string>(suggestIcao ?? aircraft?.current_base_icao ?? baseIcaos[0]?.icao ?? '');
  const [password, setPassword] = useState(''); // step-up (relocation is destructive — orphans bookings)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    const value = icao.trim().toUpperCase();
    if (!value) { setError('Enter a destination ICAO.'); return; }
    setBusy(true);
    try {
      const res = await apiFetch<{ registration: string; current_base_icao: string | null }>(
        `/api/aircraft/${encodeURIComponent(registration)}/relocate`,
        // Online path carries step_up; the offline-queue path below does NOT (the sync-replay
        // endpoint is ungated — relocation isn't an interactive admin action there).
        { method: 'POST', body: JSON.stringify({ current_base_icao: value, step_up: { password } }) },
      );
      if (res.error) { setError(res.error.message); return; }
      onDone(res.warnings);
    } catch (e) {
      // 5d-p2b: offline → queue the relocation with the device's BELIEVED current
      // location; the replay rejects-with-facts if reality has moved on (no silent
      // last-write-wins on an aircraft's location).
      if (e instanceof NetworkError && user) {
        // 5d-p5: relocation is Class A — record the advisory on the op + surface it inline.
        const readStamp = getBookingsReadStamp();
        const stale = stalenessFor('relocation', readStamp);
        const op = await enqueueOp(user.id, 'relocation', {
          registration,
          current_base_icao: value,
          expected_from: aircraft?.current_base_icao ?? '',
        }, readStamp, stale.record);
        if (op) {
          const msgs = [`Offline — relocation of ${registration} to ${value} queued. It will sync when reconnected and is rejected if the aircraft has moved meanwhile.`];
          if (stale.message) msgs.unshift(stale.message);
          onDone(msgs);
          return;
        }
        setError('Offline, and the on-device queue is unavailable — the relocation could not be saved.');
        return;
      }
      setError(e instanceof Error ? e.message : 'Request failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, width: '100%', maxWidth: 380, fontFamily: F }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>Relocate {registration}</div>
        <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 14 }}>
          Currently at <strong>{aircraft?.current_base_icao ?? 'unknown'}</strong>. Move its listing to another base.
        </div>

        <Field label="Destination base">
          <select value={baseIcaos.some((b) => b.icao === icao) ? icao : '__other__'} onChange={(e) => { if (e.target.value !== '__other__') setIcao(e.target.value); else setIcao(''); }} style={inputStyle}>
            {baseIcaos.map((b) => (
              <option key={b.id} value={b.icao as string}>{b.name} ({b.icao})</option>
            ))}
            <option value="__other__">Other ICAO…</option>
          </select>
        </Field>

        {!baseIcaos.some((b) => b.icao === icao) && (
          <Field label="ICAO (off-base / other)">
            <input value={icao} onChange={(e) => setIcao(e.target.value.toUpperCase())} placeholder="e.g. YJAB" maxLength={8} style={inputStyle} />
          </Field>
        )}

        <StepUpFields password={password} setPassword={setPassword} />

        {error && (
          <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 6, padding: '8px 10px', fontSize: 11, color: C.red, marginTop: 8, marginBottom: 8 }}>⚠ {error}</div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <button onClick={onClose} disabled={busy} style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: 6, padding: '9px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          <button onClick={submit} disabled={busy || !password} style={{ flex: 2, background: C.navy, border: 'none', color: '#fff', borderRadius: 6, padding: '9px', fontSize: 12, fontWeight: 700, cursor: busy || !password ? 'default' : 'pointer', opacity: busy || !password ? 0.7 : 1 }}>
            {busy ? 'Relocating…' : 'Relocate'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
